import React, { useEffect } from 'react';
import {
  Box,
  Drawer,
  List,
  IconButton,
  Divider,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import SidebarItem from './SidebarItem';
import './Sidebar.scss';
import { defaultSidebarItems } from './SidebarConfig';
import { useAuth } from '../../store/authStore';

const drawerWidth = 240;

export interface SidebarItem {
  path: string;
  label: string;
  icon: React.ReactNode;
}

interface SidebarProps {
  open: boolean;
  toggleSidebar: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ open, toggleSidebar }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { logout } = useAuth();

  useEffect(() => {
    if (isSmallScreen && open) {
      toggleSidebar();
    }
  }, [isSmallScreen, open, toggleSidebar]);

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout failed:', error);
      // Still navigate to login even if API call fails
      navigate('/login');
    }
  };

  const isActive = (path: string) => location.pathname === path;

  return (
    <Drawer
      variant="permanent"
      open={open}
      className="sidebarDrawer"
      sx={{
        width: open ? drawerWidth : 64,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? drawerWidth : 64,
          boxSizing: 'border-box',
        },
      }}
    >
      <Box className="sidebarToggleContainer" mb={1}>
        <IconButton
          onClick={toggleSidebar}
          className="sidebarToggleButton"
          sx={{
            '&:hover': {
              backgroundColor: 'rgba(238, 238, 238, 0.1)', // subtle light hover effect
            },
            '&:focus': {
              outline: 'none', // removes the focus outline
            },
          }}
        >
          {open ? (
            <ChevronLeftIcon
              sx={{
                color: '#EEEEEE',
              }}
            />
          ) : (
            <ChevronRightIcon
              sx={{
                color: '#EEEEEE',
              }}
            />
          )}
        </IconButton>
      </Box>

      <Divider className="sidebarDivider" />

      <Box className="sidebarContent">
        <List>
          {defaultSidebarItems.map((item, index) => (
            <SidebarItem
              key={index}
              item={item}
              isActive={isActive(item.path)}
              open={open}
              onClick={() => handleNavigation(item.path)}
            />
          ))}
        </List>
      </Box>

      <Box className="sidebarBottom">
        <Divider className="sidebarDivider" />
        <Box className="sidebarLogoutContainer">
          <IconButton
            onClick={handleLogout}
            className="sidebarLogoutButton"
            sx={{
              width: '100%',
              borderRadius: 0,
              padding: '12px',
              justifyContent: open ? 'flex-start' : 'center',
              '&:hover': {
                backgroundColor: 'rgba(238, 238, 238, 0.1)',
              },
              '&:focus': {
                outline: 'none',
              },
            }}
          >
            <LogoutIcon
              sx={{
                color: '#EEEEEE',
                marginRight: open ? '12px' : '0',
              }}
            />
            {open && <span className="sidebarLogoutText">Logout</span>}
          </IconButton>
        </Box>
      </Box>
    </Drawer>
  );
};

export default Sidebar;
